// Clipboard adapter, part 1 of 2: Excel puts a copied range on the clipboard as tab-separated text
// (rows end with CRLF; a cell containing a tab, line break or quote is wrapped in quotes, with
// inner quotes doubled). Only `text/plain` is read: HTML is never interpreted, and nothing is
// evaluated. Part 2 (placing the table into the grid) is `applyPaste` in pending.ts.

export const MAX_PASTE_ROWS = 1000;
export const MAX_PASTE_COLUMNS = 20;

export type ClipboardTable = string[][];

export class PasteTooLargeError extends Error {
  override readonly name = 'PasteTooLargeError';
}

/**
 * Parses clipboard text into rows of cells. A final line break (Excel always adds one) is not a
 * row, and trailing blank rows are dropped. Cells are kept exactly as text: leading zeros stay.
 */
export function parseClipboardTable(text: string): ClipboardTable {
  const rows: ClipboardTable = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  let cellStarted = false;
  const endCell = () => {
    row.push(cell);
    cell = '';
    cellStarted = false;
  };
  const endRow = () => {
    endCell();
    rows.push(row);
    row = [];
    if (rows.length > MAX_PASTE_ROWS)
      throw new PasteTooLargeError(`more than ${String(MAX_PASTE_ROWS)} rows`);
  };

  for (let i = 0; i < text.length; i += 1) {
    const char = text.charAt(i);
    if (quoted) {
      if (char === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"' && !cellStarted) {
      quoted = true;
      cellStarted = true;
    } else if (char === '\t') {
      endCell();
    } else if (char === '\r' || char === '\n') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      endRow();
    } else {
      cell += char;
      cellStarted = true;
    }
  }
  if (cellStarted || cell !== '' || row.length > 0) endRow();
  // Blank rows at the end of a copied Excel range carry no data; pasting them over stored units
  // would only clear their cells by accident, so they are dropped. Blank rows in the middle stay.
  while (rows.length > 0 && (rows.at(-1) ?? []).every((value) => value === '')) rows.pop();
  if (rows.some((r) => r.length > MAX_PASTE_COLUMNS)) {
    throw new PasteTooLargeError(`more than ${String(MAX_PASTE_COLUMNS)} columns`);
  }
  return rows;
}
